// #2302: a request's detail read, started when the operator presses on its row. The click that
// follows opens the workspace, which takes over this read instead of starting the same one a moment
// later. The handover is its only use, so the workspace still never shows a remembered answer
// (#1536): the read is claimed once, only for the same request in the same business, only while no
// change call, sign-in, sign-out or business switch happened after it started, and only shortly.
import type { CockpitClient } from "./client";
import { getTenantContext } from "./http-client";
import { readCacheGeneration } from "./read-cache";
import type { RequestDetail } from "./types";

/** Press to workspace mount is well under a second; an older read is not handed over. */
const CLAIM_WITHIN_MS = 3_000;

interface Prefetch {
  key: string;
  generation: number;
  at: number;
  promise: Promise<RequestDetail>;
}

let pending: Prefetch | null = null;

const keyOf = (ref: string) => JSON.stringify([getTenantContext(), ref]);

function claimable(read: Prefetch | null, key: string): read is Prefetch {
  return read !== null && read.key === key && read.generation === readCacheGeneration()
    && Date.now() - read.at <= CLAIM_WITHIN_MS;
}

/** Starts the detail read of request `ref` (its number or id), unless the same one is under way. */
export function prefetchRequest(client: Pick<CockpitClient, "getRequest">, ref: string): void {
  const key = keyOf(ref);
  if (claimable(pending, key)) return;
  const promise = client.getRequest(ref);
  // A failure surfaces in the workspace, which reads again; it is never an unhandled rejection.
  promise.catch(() => undefined);
  pending = { key, generation: readCacheGeneration(), at: Date.now(), promise };
}

/** The read started for exactly this request, once; otherwise null and the caller reads itself. */
export function claimPrefetchedRequest(ref: string): Promise<RequestDetail> | null {
  const read = pending;
  pending = null;
  return claimable(read, keyOf(ref)) ? read.promise : null;
}
